import { useEffect, useState } from "react";

import { Button, Select, Typography, useTable, TableMetadata, TableStateWithFilters, ColumnSortEnum, Table, ArrowButton } from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchNotifications } from "../../api/nisooApi";
import { useInput, useDidUpdateEffect } from "../../hooks";
import { Notification, NotificationAdministrationSearch, NotificationSort } from "../../models/Notification";
import { SortDirection } from "../../models/Shared";
import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetNotificationRolesQuery, useGetUserRolesQuery } from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { notificationNewRoute, notificationDetailUrl, notificationRoute } from "../../routes/routes";
import styles from "./NotificationList.module.scss";


export function NotificationAdministration() {

    const history = useHistory();
    const { t } = useTranslation();
    const { handleChange } = useInput();
    const { data: userSession } = useGetUserSessionQuery();
    const storageKey = "NotificationAdministrationSearch";

    const createInitialTableState = (): TableStateWithFilters<NotificationAdministrationSearch> => {

        const sessionStorageTableState = sessionStorage.getItem(storageKey);
        if (sessionStorageTableState === null) {
            return {
                roleIds: [],
                isActive: undefined,
                page: 1,
                pageSize: 10,
                sortBy: NotificationSort.Title,
                order: ColumnSortEnum.ASC
            };
        }

        var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<NotificationAdministrationSearch>;

        return result;
    };

    const createInitialNotifications = (): PagedResult<Notification> => {
        return {
            currentPage: 1,
            firstRowOnPage: 0,
            lastRowOnPage: 0,
            pageCount: 0,
            pageSize: 10,
            rowCount: 0,
            results: []
        }
    };

    const createInitialNotificationSearch = (initialTableState: TableStateWithFilters<NotificationAdministrationSearch>): NotificationAdministrationSearch => {
        return {
            roleIds: initialTableState.roleIds,
            isActive: initialTableState.isActive
        }
    };


    // const {data: notificationRoles} = useGetNotificationRolesQuery();
    const { data: notificationRoles } = useGetUserRolesQuery();
    const [initialTableState] = useState<TableStateWithFilters<NotificationAdministrationSearch>>(() => createInitialTableState());
    const [notifications, setNotifications] = useState<PagedResult<Notification>>(createInitialNotifications());
    const [notificationSearch, setNotificationSearch] = useState<NotificationAdministrationSearch>(createInitialNotificationSearch(initialTableState));


    const notificationsTableMetada: TableMetadata<Notification> = {
        rowKey: "id",
        columns: [
            {
                columnKey: "title",
                header: t("notification.title"),
                accessor: "title",
                sortBy: NotificationSort.Title
            },
            {
                columnKey: "validFrom",
                header: t("notification.validFrom"),
                accessor: (item) =>
                    new Date(item.validFrom!).toLocaleString("hr-HR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                    }),
                sortBy: NotificationSort.ValidFrom
            },
            {
                columnKey: "putOnTop",
                header: t("notification.putOnTop"),
                accessor: (item) => {
                    return <div>{item.putOnTop ? t("common.yes") : t("common.no")}</div>
                },
                sortBy: NotificationSort.PutOnTop
            },
            {
                columnKey: "roles",
                header: t("notification.intendedForRoles"),
                accessor: (item) => {
                    return <div>{getRoles(item)}</div>
                }
            },
            {
                columnKey: "isActive",
                header: t("common.status"),
                accessor: (item) => {
                    return <div>{item.isActive ? t("notification.active") : t("notification.inactive")}</div>
                },
                sortBy: NotificationSort.IsActive
            },
            {
                columnKey: "details",
                header: "",
                accessor: (item) => {
                    return (
                        <div className={"text-right"}>
                            <ArrowButton ariaLabel={t("notification.goToNotificationDetails")}
                                onClick={() => history.push(notificationDetailUrl(item.id))}
                            />
                        </div>
                    )
                }
            }

        ],
        rowToolTip: () => undefined
    };

    var [tableProps, tableState, setFilters] = useTable<NotificationAdministrationSearch>({
        metadata: notificationsTableMetada,
        data: notifications.results,
        pagination: true,
        paginationPage: initialTableState.page,
        paginationPageSize: initialTableState.pageSize as any,
        totalItems: notifications.rowCount,
        defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }

    });

    const convertNotificationAdministrationSearchToApiRequest = (notificationSearch: NotificationAdministrationSearch) => {
        var request = {
            roleIds: notificationSearch.roleIds,
            isActive: notificationSearch.isActive,
            page: tableState.page ?? 1,
            pageSize: tableState.pageSize ?? 10,
            sortBy: tableState.sortBy ?? NotificationSort.Title,
            sortDirection: tableState.order && tableState.order === ColumnSortEnum.ASC ? SortDirection.Asc : SortDirection.Desc
        };
        return request;
    };

    useEffect(() => {

        if (!userSession || (userSession.roleId !== RoleEnum.ASOO && userSession.roleId !== RoleEnum.ASOOAdmin && userSession.roleId !== RoleEnum.ASOOOffice
            && userSession.roleId !== RoleEnum.MZOM && userSession.roleId !== RoleEnum.MZOMAdmin && userSession.roleId !== RoleEnum.MZOMOffice
            && userSession.roleId !== RoleEnum.Helpdesk)) {
            return;
        }

        const search = async () => {
            const request = convertNotificationAdministrationSearchToApiRequest(notificationSearch);
            var results = await searchNotifications(request);

            setNotifications(results);
        };

        search();
    }, [tableState, userSession]);

    const onCreateNotificationClick = () => {
        history.push(notificationNewRoute());
    };

    useDidUpdateEffect(() => {
        sessionStorage.setItem(storageKey, JSON.stringify({
            ...notificationSearch,
            sortBy: tableState.sortBy ?? NotificationSort.Title,
            order: tableState.order ?? ColumnSortEnum.ASC,
            page: tableState.page ?? 1,
            pageSize: tableState.pageSize ?? 10
        }));
    }, [tableState]);

    const statusList = () => {
        return [
            { id: 1, name: t("notification.active") },
            { id: 2, name: t("notification.inactive") }
        ];
    };

    const handleStatusChange = (e: any) => {
        const status = Number.parseInt(e.target.value);
        const newNotificationAdministrationSearch: NotificationAdministrationSearch = {
            ...notificationSearch,
            isActive: !isNaN(status) ? (status === 1 ? true : false) : undefined
        };

        setNotificationSearch(newNotificationAdministrationSearch);
    };

    const getRoles = (item: Notification) => {
        var roles = item.roles?.map((val) => (
            <li>
                <Typography component="span" variant="small">
                    {notificationRoles?.find((nr) => nr.id === val)?.name}
                </Typography>
            </li>
        ));
        return <ul>{roles}</ul>;
    };

    const clearFilters = () => {
        var emptySearch = {
            roleIds: [],
            isActive: undefined
        };
        setNotificationSearch(emptySearch);
        setFilters(emptySearch);
    };

    const searchButtonClick = () => {
        setFilters(notificationSearch);
    };

    const onBackToNotificationListClick = () => {
        history.push(notificationRoute());
    };

    return (
        <>
            <Row>
                <Col md={6}>
                    <ArrowButton
                        backwards
                        ariaLabel={t("common.returnToNotificationsList")}
                        onClick={onBackToNotificationListClick}
                    />
                    <span className={styles.backButtonCaption}>
                        {t("common.returnToNotificationsList")}
                    </span>
                </Col>
            </Row>
            <br />
            <Row>
                <Col md={10}>
                    <h2>{t("navigation.notificationsAdministration")}</h2>
                </Col>
                <Col md={2}>
                    <Button
                        outline
                        icon={<AddIcon />}
                        onClick={onCreateNotificationClick}
                        className={styles.actionButton}
                    >
                        {t("common.addNew")}
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <Select
                        multiple
                        label={t("notification.intendedForRoles")}
                        name={nameof<typeof notificationSearch>("roleIds")}
                        value={notificationSearch.roleIds}
                        onChange={handleChange(setNotificationSearch)}
                        selectData={codeBookToSelectData(notificationRoles)}
                    />
                </Col>
                <Col md={4}>
                    <Select
                        label={t("common.status")}
                        name={nameof<typeof notificationSearch>("isActive")}
                        value={notificationSearch.isActive !== undefined ? (notificationSearch!.isActive === true ? 1 : 2) : undefined}
                        onChange={handleStatusChange}
                        selectData={codeBookToSelectData(statusList())}
                        emptyOption={{ value: undefined, text: undefined }}
                    />
                </Col>
            </Row>
            <Row className="mb-5 mt-5">
                <Col md={12} className="d-flex justify-content-end align-items-center">
                    <Button
                        onClick={searchButtonClick}
                    >
                        {t("common.search")}
                    </Button>
                    <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={clearFilters}>
                        {t("common.clearFilters")}
                    </Typography>
                </Col>
            </Row>
            <Table {...tableProps} />
        </>
    );
};